/**
 * The quiet sibling of `services/api.ts`.
 *
 * Both talk to the same backend with the same auth and the same session
 * handling. The only difference is that this one raises no toasts: the pages
 * built on it report their own errors, and two messages for one failure is
 * worse than none.
 *
 * It used to be a second axios instance with its own token-refresh
 * implementation - a duplicate that drifted from the one in services/api.ts and
 * had to be fixed twice for the same bug. There is one refresh path now, in
 * utils/sessionRefresh.ts, and both clients call it.
 */

import { createHttpClient, HttpError, HttpRequestConfig } from "@/lib/http";
import { getAuthToken } from "@/utils/authToken";
import {
  endSession,
  isAuthEndpoint,
  isSessionProblem,
  refreshSession,
} from "@/utils/sessionRefresh";
import { handlePaymentRequired } from "@/utils/paymentRequired";
import { handleConsentRequired } from "@/utils/consentRequired";

const apiClient = createHttpClient({
  baseURL: import.meta.env.VITE_API_URL || "/api",
  timeout: 30000,
});

apiClient.interceptors.request.use((config: HttpRequestConfig) => {
  const token = getAuthToken();
  if (token) {
    config.headers = { ...config.headers, Authorization: `Bearer ${token}` };
  }
  return config;
});

apiClient.interceptors.response.use(undefined, async (error: unknown) => {
  // A paywall is not a page-level error even here: the pages built on this
  // client report their own failures, but none of them can offer a plan.
  if (handlePaymentRequired(error)) throw error;
  // Likewise a missing consent: ConsentGate offers it in place.
  if (handleConsentRequired(error)) throw error;

  if (!(error instanceof HttpError) || error.response?.status !== 401) {
    throw error;
  }

  // A 401 saying the password for this action was wrong is not a session
  // problem: refreshing cannot fix it and replaying only resends the wrong
  // password, after which the retry guard would sign the user out for a typo.
  if (!isSessionProblem(error.response.data)) throw error;

  const original = error.config as HttpRequestConfig & {
    _sessionRetried?: boolean;
  };

  if (original && !original._sessionRetried && !isAuthEndpoint(original.url)) {
    original._sessionRetried = true;
    const token = await refreshSession();
    if (token) {
      original.headers = {
        ...original.headers,
        Authorization: `Bearer ${token}`,
      };
      return apiClient.request(original);
    }
  }

  void endSession();
  throw error;
});

export default apiClient;
